import React, { useState, useEffect, useCallback, useRef } from "react";
import { View, TouchableOpacity, Text } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { FlatList } from "react-native-gesture-handler";
import { AppText } from "@/components/AppText";
import {
  useTranscriptWebSocket,
  CustomerMessage,
  ServiceProviderMessage,
  CallStatusMessage,
} from "@/hooks/useTranscriptWebSocket";
import {
  RefundProcessItem,
  getPlatformName,
} from "@/utils/refundProcessTypeGuards";
import RippleAnimation from "@/components/RippleAnimation";

type Props = {
  item: RefundProcessItem;
  sessionId: string;
  onClose: () => void;
};

const CallInProgressContent: React.FC<Props> = ({
  item,
  sessionId,
  onClose,
}) => {
  const [transcriptMessages, setTranscriptMessages] = useState<
    (CustomerMessage | ServiceProviderMessage)[]
  >([]);
  const [callEnded, setCallEnded] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const flatListRef = useRef<FlatList>(null);

  const platformName = getPlatformName(item);

  // Callback to handle message updates - merge messages with same ID
  const handleMessageUpdate = useCallback(
    (message: CustomerMessage | ServiceProviderMessage) => {
      console.log("Received message:", message);
      setTranscriptMessages((prevMessages) => {
        // Check if message with same ID exists
        const existingIndex = prevMessages.findIndex(
          (msg) => msg.messageId === message.messageId
        );

        if (existingIndex !== -1) {
          // Message exists, merge the text
          const updatedMessages = [...prevMessages];
          updatedMessages[existingIndex] = {
            ...updatedMessages[existingIndex],
            text: updatedMessages[existingIndex].text + message.text,
          };
          return updatedMessages;
        } else {
          // New message, append it
          return [...prevMessages, message];
        }
      });
    },
    []
  );

  // WebSocket hook for transcript
  useTranscriptWebSocket({
    sessionId,
    onConnected: (message) => {
      console.info("Connected to session:", message.sessionId);
    },
    onCallStatus: (message: CallStatusMessage) => {
      console.log("Call status update:", message.status, message.message);

      // Handle call ended - show completion state
      if (message.status === "ended") {
        console.log("Call ended");
        setCallEnded(true);

        // Auto-close modal after 2 seconds
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    },
    onCustomerMessage: handleMessageUpdate,
    onServiceProviderMessage: handleMessageUpdate,
    onConnectionChange: (connected) => {
      console.log("WebSocket connection status:", connected);
    },
    onError: (error) => {
      console.error("WebSocket error:", error);
    },
  });

  // Clear transcript when sessionId changes
  useEffect(() => {
    setTranscriptMessages([]);
    setCallEnded(false);
    setShouldAutoScroll(true);
  }, [sessionId]);

  // Auto-scroll to bottom when new messages arrive (only if shouldAutoScroll is true)
  useEffect(() => {
    if (transcriptMessages.length > 0 && shouldAutoScroll) {
      setTimeout(() => {
        flatListRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  }, [transcriptMessages, shouldAutoScroll]);

  // Handle scroll events to detect manual scrolling
  const handleScroll = useCallback(
    (event: any) => {
      const { contentOffset, contentSize, layoutMeasurement } =
        event.nativeEvent;
      const isAtBottom =
        contentOffset.y >= contentSize.height - layoutMeasurement.height - 20;

      // If user scrolled to bottom, enable auto-scroll
      if (isAtBottom && !shouldAutoScroll) {
        setShouldAutoScroll(true);
      } else if (!isAtBottom && shouldAutoScroll) {
        // If user scrolled up, disable auto-scroll
        setShouldAutoScroll(false);
      }
    },
    [shouldAutoScroll]
  );

  return (
    <>
      {/* Content */}
      <View style={styles.contentContainer}>
        <View style={styles.contentInner}>
          {/* Ripple Animation */}
          <RippleAnimation active={!callEnded} />

          {/* Text content */}
          <View style={styles.textContainer}>
            <AppText style={styles.title}>
              We&apos;re calling {platformName}&apos;s support line
            </AppText>

            <AppText style={styles.description}>
              You can listen in or close this window and we&apos;ll notify you
              with the result. Your audio will not be used for the call.
            </AppText>
          </View>
        </View>
      </View>

      {/* Conversation Box */}
      <View style={styles.conversationBox}>
        <FlatList
          ref={flatListRef}
          data={transcriptMessages}
          keyExtractor={(item) => item.messageId}
          renderItem={({ item }) => (
            <View style={styles.messageRow}>
              <Text style={styles.messageText}>
                {item.type === "customer" ? "AI Agent: " : "Booking platform: "}
                {item.text}
              </Text>
            </View>
          )}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={true}
          scrollEnabled={true}
          nestedScrollEnabled={true}
          onScroll={handleScroll}
          scrollEventThrottle={16}
        />
      </View>

      {/* Close Button */}
      <TouchableOpacity style={styles.closeButton} onPress={onClose}>
        <AppText style={styles.closeButtonText}>Close</AppText>
      </TouchableOpacity>
    </>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    gap: 16,
    paddingTop: 8,
  },
  contentInner: {
    alignItems: "center",
    gap: 4,
  },
  textContainer: {
    gap: 12,
    alignItems: "center",
  },
  title: {
    fontSize: 24,
    fontWeight: "500",
    color: "#000000",
    textAlign: "center",
    lineHeight: 30,
  },
  description: {
    fontSize: 15,
    fontWeight: "400",
    color: "#000000",
    opacity: 0.5,
    textAlign: "center",
    lineHeight: 23,
    letterSpacing: -0.3,
  },
  conversationBox: {
    backgroundColor: "#F3F3F3",
    borderRadius: 16,
    padding: 20,
    height: 153,
    alignSelf: "stretch",
    marginTop: 16,
    marginBottom: 8,
  },
  scrollContent: {
    flexGrow: 1,
  },
  messageRow: {
    marginBottom: 8,
  },
  messageText: {
    fontSize: 15,
    fontWeight: "400",
    color: "#797F87",
    lineHeight: 23,
    letterSpacing: -0.3,
  },
  closeButton: {
    backgroundColor: "#F4F5F6",
    borderRadius: 100,
    paddingHorizontal: 20,
    height: 58,
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    marginTop: 8,
  },
  closeButtonText: {
    color: "#919191",
    fontSize: 17,
    fontWeight: "600",
    textAlign: "center",
    lineHeight: 22,
  },
});

export { CallInProgressContent };
